import { MenuList } from './menu.interfaces';
import { Injectable } from '@angular/core';
import { UserUtils } from '@user/service/user-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleNode } from '../../components/article/presenter/article-node';
import { IsProjectType } from '../../model/is-project-type';
import { ProjectUtils } from '../../components/project/util/project-utils';
import { UserModel } from '@user/model/user.model';
import { ProjectModel } from '../../model/project.model';
import { DataSheetService } from '../../import-export/dataSheet/data-sheet.service';
import { ProjectComponentPresenter } from '../../components/project-component.presenter';
import { ArticleComponentsPresenter } from '@admintool/components/presenter/article-components.presenter';
import { ProjectItemListHelper } from '../../helper/project-item-list-helper';
import { getConfiguratorViewInfo } from '@configurator/view/configurator-view-info';
import { ReportVO } from '../../model/vo/report.vo';
import { SelectProjectReportService } from '../../components/report/service/select-project-report.service';
import { BackgroundTasksInfoService } from '../background-tasks-info.service';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { StringUtils } from '@shared/util/string-utils';

@Injectable({providedIn: 'root'})
export class DocumentationMenuCreator {

  constructor(
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private dataSheetService: DataSheetService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private articleComponentsPresenter: ArticleComponentsPresenter,
    private projectItemListHelper: ProjectItemListHelper,
    private selectProjectReportService: SelectProjectReportService,
    private backgroundTasksInfoService: BackgroundTasksInfoService,
    private systemConfigurationService: SystemConfigurationService,
  ) {}

  public create(): { id: string, content: MenuList } {
    return  { id: 'header-menu-documentation', content: this.getDocumentationItems() };
  }

  private getDocumentationItems(): MenuList {
    const items: MenuList = [
      {
        label: 'header.menu.create_data_sheet',
        action: () => this.openCustomDataSheet(),
        enabled: true,
        visible: this.isCustomDataSheetVisible(),
        appActionLogId: 'header.menu.create_data_sheet',
        id: 'header-menu-project-data-sheet',
      },
      {
        label: 'header.menu.create_documentation',
        action: () => this.generatePdfReport(),
        enabled: this.isPrintProjectEnabled() && !this.backgroundTasksInfoService.isTaskActive("pdf-export"),
        visible: this.isPrintProjectVisible(),
        appActionLogId: 'header.menu.create_documentation',
        id: 'header-menu-project-pdf',
      },
      {
        label: 'header.menu.dataCenter',
        action: () => this.actionWithCdd(),
        enabled: this.isCddEnabled(),
        visible: this.isCddVisible(),
        appActionLogId: 'header.menu.dataCenter',
        id: 'header-menu-export-datacenter',
      },
      {
        label: 'header.menu.cirqit',
        action: () => this.actionWithCirqit(),
        enabled: this.isCirqitEnabled(),
        visible: this.isCirqitVisible(),
        appActionLogId: 'header.menu.cirqit',
        id: 'header-menu-import-cirqit',
      },
    ];

    return items;
  }

  private async openCustomDataSheet(): Promise<void> {
    if (UserUtils.hasEditProjectPermission(this.projectModel.user) && this.projectModel.currentProjectRequiresSaving) {
      const saved = await this.projectComponentPresenter.openSaveProjectPopUp();
      if (saved) {
        await this.dataSheetService.openCustomDataSheet();
      }
    } else {
      await this.dataSheetService.openCustomDataSheet();
    }
  }

  private isCustomDataSheetVisible(): boolean {
    return this.isDataSheetFeatureEnabled() && this.dataSheetService.canOpenCustomDataSheet();
  }

  private actionWithCdd(): void {
    const articles: ArticleVO[] = this.projectItemListHelper.getItemNodeList().map((articleNode: ArticleNode) => articleNode.article);
    this.articleComponentsPresenter.openCompactDataDownloadDialog(articles);
  }

  private isCddEnabled(): boolean {
    return ( this.isProjectNotEmpty() && IsProjectType.among(this.projectModel.currentProject,
      'FREE_MOUNTING', 'MOUNTING_RAIL', 'SPEEDWAY', 'XCOM', 'CIRCUIT_BOARD'));
  }

  private isProjectNotEmpty(): boolean {
    return !ProjectUtils.isEmptyProject(this.projectModel.currentProject);
  }

  private isCddVisible(): boolean {
    return getConfiguratorViewInfo(this.projectModel.currentProjectType).menu.interfaceMenuItemsVisibility['exportDataCenter']
      && this.userModel.userProfile.isRegisteredUserAccount() && !this.userModel.userProfile.user.isProduction()
      && IsProjectType.controlCabinet(this.projectModel.currentProject);
  }

  private isDataSheetFeatureEnabled(): boolean {
    return this.userModel.user && this.userModel.user.hasPermission("GENERATE_DATA_SHEET") &&
      this.projectModel.currentProject &&
      (IsProjectType.mcs(this.projectModel.currentProject.projectType) || this.userModel.user.hasPermission("DEV_TEST"));
  }

  private async generatePdfReport(): Promise<void> {
    if (UserUtils.hasEditProjectPermission(this.projectModel.user) && this.projectModel.currentProjectRequiresSaving) {
      const saved = await this.projectComponentPresenter.openSaveProjectPopUp();
      if (saved) {
        this.openReportPopup();
      }
    } else {
      this.openReportPopup();
    }
  }

  private openReportPopup(): void {
    this.selectProjectReportService.openProjectReportPopup(ReportVO.TYPE_PDF);
  }

  private isPrintProjectEnabled(): boolean {
    if (!this.userModel.user || !ProjectUtils.isProjectPrintable(this.projectModel.currentProject)) {
      return false;
    }
    // user must save project before report generation, so SAVE_PROJECTS permission is required
    // in case when user doesn't have EDIT_PROJECT permission we consider that he can't change loaded project so we can generate report for
    // this project without saving
    if (!UserUtils.hasEditProjectPermission(this.userModel.user)) {
      return true;
    }
    // in case user is guest, we enable print button, to allow for prompting him to login after he clicked on it
    if (!this.userModel.user.isRegisteredUserAccount()) {
      return true;
    }
    return this.userModel.user.hasPermissions(["SAVE_PROJECTS", "GENERATE_REPORTS"]);
  }

  private isPrintProjectVisible(): boolean {
    if(IsProjectType.mcsGds(this.projectModel.currentProject) && this.isDataSheetFeatureEnabled()) {
      return this.userModel.user.hasPermission("GENERATE_REPORTS_FOR_MCSGDS");
    }

    return (
      (!this.userModel.user.isRegisteredUserAccount() || this.userModel.user.hasPermission("GENERATE_REPORTS")) &&
      !this.projectModel.isProjectOpenedByTemporaryLink
    );
  }

  private actionWithCirqit(): void {
    const cirqitUrl = this.systemConfigurationService.currentConfiguration?.cirqitUrl;
    if (StringUtils.isNotEmpty(cirqitUrl)) {
      window.open(cirqitUrl, '_blank');
    }
  }

  private isCirqitEnabled(): boolean {
    const cirqitUrl = this.systemConfigurationService.currentConfiguration?.cirqitUrl;
    return StringUtils.isNotEmpty(cirqitUrl) && IsProjectType.controlCabinet(this.projectModel.currentProject);
  }

  private isCirqitVisible(): boolean {
    return this.isCirqitEnabled();
  }
}
